import { formatBrl } from './money.js';

/**
 * estac1 plan pricing — single source of truth.
 *
 * Model: flat monthly price per vacancy band (like the flat tiers the market
 * uses), chosen to undercut the competitors' entry price at every band while
 * SMS lives in its own usage-billed add-on:
 *
 *   1–30 vagas:    R$ 39,90/mês
 *   31–100 vagas:  R$ 59,90/mês
 *   101–150 vagas: R$ 79,90/mês
 *   >150:          contact us (returns CAP_EXCEEDED)
 *
 * Trial: 7 days, no card. Annual cycle charges 10 months (2 free).
 *
 * The old model (R$ 84,90 minimum + graduated per-vacancy rates) embedded the
 * SMS cost in the subscription; with delivery defaulting to QR/WhatsApp share
 * and SMS metered separately, the base can be flat and cheap.
 */

export interface PricingTier {
  /** Inclusive lower bound of vacancies in this band. */
  minVacancies: number;
  /** Inclusive upper bound. The last band sets the absolute cap. */
  maxVacancies: number;
  /** Flat monthly price for lots whose vacancy count falls in this band. */
  monthlyCents: number;
}

export const PRICING_TIERS: readonly PricingTier[] = [
  { minVacancies: 1, maxVacancies: 30, monthlyCents: 3990 },
  { minVacancies: 31, maxVacancies: 100, monthlyCents: 5990 },
  { minVacancies: 101, maxVacancies: 150, monthlyCents: 7990 },
] as const;

/** Floor: no quote goes below the first band's price. */
export const PRICING_MINIMUM_CENTS = 3990;
export const PRICING_MAX_VACANCIES = 150;
export const TRIAL_DAYS = 7;
/** Days the painel stays open after an unpaid trial ends, before it is blocked. */
export const TRIAL_BLOCK_GRACE_DAYS = 3;
/** Days without any use, after an unpaid trial ends, before the lot is archived. */
export const TRIAL_ARCHIVE_AFTER_DAYS = 7;

/** Runtime shape guard — settings may carry the pre-band tier shape
 * (perVacancyCents) from an old KV cache or DB row; those must not reach the
 * band math. */
export const isPricingTier = (v: unknown): v is PricingTier =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as PricingTier).minVacancies === 'number' &&
  typeof (v as PricingTier).maxVacancies === 'number' &&
  typeof (v as PricingTier).monthlyCents === 'number';

export interface QuoteOverrides {
  /** Custom bands (otherwise uses PRICING_TIERS). */
  tiers?: readonly PricingTier[];
  /** Custom minimum (otherwise uses PRICING_MINIMUM_CENTS). */
  minimumCents?: number;
  /** Custom cap (otherwise uses PRICING_MAX_VACANCIES). */
  maxVacancies?: number;
}

export interface QuoteInput extends QuoteOverrides {
  vacancies: number;
}

export interface QuoteBreakdownLine {
  label: string;
  amountCents: number;
}

export type QuoteResult =
  | {
      ok: true;
      vacancies: number;
      /** The matched band's price, before the floor. */
      subtotalCents: number;
      /** Floor minimum applied (if the band price was below it). */
      minimumCents: number;
      minimumApplied: boolean;
      /** What the customer pays. Either the band price or the floor, whichever is greater. */
      totalCents: number;
      breakdown: QuoteBreakdownLine[];
      /** Convenience: localized human-readable representation, useful for emails. */
      label: string;
    }
  | {
      ok: false;
      reason: 'INVALID_VACANCIES' | 'CAP_EXCEEDED';
      message: string;
    };

/**
 * Pure pricing calculator. No side effects, no I/O. Safe to call from anywhere.
 *
 * Pricing model: the declared vacancy count selects a band; the quote is that
 * band's flat monthly price (with the global minimum as a floor guard).
 *
 * @param input.vacancies declared vacancy count (positive integer)
 */
export const calculateQuote = (input: QuoteInput): QuoteResult => {
  const provided = input.tiers ?? PRICING_TIERS;
  // Falls back wholesale if any entry is malformed (e.g., stale settings with
  // the old per-vacancy shape) — a partial table would misprice silently.
  const tiers = provided.length > 0 && provided.every(isPricingTier) ? provided : PRICING_TIERS;
  const minimumCents = input.minimumCents ?? PRICING_MINIMUM_CENTS;
  const maxVacancies = input.maxVacancies ?? PRICING_MAX_VACANCIES;

  if (!Number.isInteger(input.vacancies) || input.vacancies <= 0) {
    return {
      ok: false,
      reason: 'INVALID_VACANCIES',
      message: 'Quantidade de vagas inválida.',
    };
  }
  if (input.vacancies > maxVacancies) {
    return {
      ok: false,
      reason: 'CAP_EXCEEDED',
      message: `Acima de ${maxVacancies} vagas, fale com a gente para um plano sob medida.`,
    };
  }

  // Exact band match first; if custom bands leave gaps, take the cheapest band
  // that still covers the count so a misconfiguration never undercharges.
  const band =
    tiers.find((t) => input.vacancies >= t.minVacancies && input.vacancies <= t.maxVacancies) ??
    [...tiers]
      .filter((t) => t.maxVacancies >= input.vacancies)
      .sort((a, b) => a.monthlyCents - b.monthlyCents)[0];
  if (!band) {
    return {
      ok: false,
      reason: 'CAP_EXCEEDED',
      message: `Acima de ${maxVacancies} vagas, fale com a gente para um plano sob medida.`,
    };
  }

  const subtotalCents = band.monthlyCents;
  const totalCents = Math.max(subtotalCents, minimumCents);

  return {
    ok: true,
    vacancies: input.vacancies,
    subtotalCents,
    minimumCents,
    minimumApplied: totalCents > subtotalCents,
    totalCents,
    breakdown: [{ label: `Plano até ${band.maxVacancies} vagas`, amountCents: totalCents }],
    label: `${input.vacancies} vagas — ${formatBrl(totalCents)}/mês`,
  };
};
